/**
 * Browser Compatibility Warning Component
 * Shows modal if browser doesn't support required features
 */

import { useEffect, useState } from 'react';
import {
  detectBrowserCapabilities,
  type BrowserCapabilities,
} from '../utils/browserCompatibility';

export function BrowserCompatibilityWarning() {
  const [capabilities, setCapabilities] = useState<BrowserCapabilities | null>(
    null
  );
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const caps = detectBrowserCapabilities();
    setCapabilities(caps);

    // Check if user already dismissed warning
    const isDismissed = localStorage.getItem('compatibility-warning-dismissed');
    if (isDismissed) {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('compatibility-warning-dismissed', 'true');
    setDismissed(true);
  };

  if (!capabilities || capabilities.compatible || dismissed) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            Browser Compatibility Issue
          </h2>
        </div>

        <div className="mb-4 space-y-2">
          <p className="text-gray-700">
            Your browser does not support some features required by WaySightAI:
          </p>
          <ul className="list-inside list-disc space-y-1 text-sm text-gray-600">
            {capabilities.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>

        <div className="mb-4 rounded-md bg-blue-50 p-3">
          <p className="text-sm text-blue-800">
            <strong>Recommended browsers:</strong> Latest versions of Chrome,
            Firefox, Safari, or Edge
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleDismiss}
            className="flex-1 rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
          >
            Continue Anyway
          </button>
          <a
            href="https://www.google.com/chrome/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-blue-700"
          >
            Get Chrome
          </a>
        </div>
      </div>
    </div>
  );
}
